import { Check, Download, IndianRupee, Loader2 } from "lucide-react";

export interface BillBulkActionBarProps {
  selectedCount: number;
  onClear: () => void;
  /**
   * Approve the selected bills — typically a Promise.allSettled over
   * `bills.transitionStatus({ status: 'ready_to_pay' })` per row that is
   * currently `approval_pending`. Rows in other statuses are skipped by
   * the caller.
   */
  onApprove: () => void;
  /** Disable Approve when no selected row is `approval_pending`. */
  approveDisabled?: boolean;
  /** True while the batch approve is in flight. */
  approving?: boolean;
}

export function BillBulkActionBar({
  selectedCount,
  onClear,
  onApprove,
  approveDisabled = false,
  approving = false,
}: BillBulkActionBarProps) {
  if (selectedCount === 0) return null;

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="flex min-h-14 flex-wrap items-center gap-3 rounded-md bg-ink px-4 py-2.5 text-bg shadow-sm"
    >
      <span className="text-sm">
        <strong className="font-mono font-semibold">{selectedCount}</strong> bill
        {selectedCount === 1 ? "" : "s"} selected
      </span>
      <span className="ml-auto" />
      <button
        type="button"
        onClick={onApprove}
        disabled={approveDisabled || approving}
        title={
          approveDisabled
            ? "No approval-pending bills in the current selection."
            : "Approve the selected bills (skips rows not awaiting approval)."
        }
        className="inline-flex h-9 items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-medium text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white/10"
      >
        {approving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        {approving ? "Approving…" : "Approve selected"}
      </button>
      <button
        type="button"
        disabled
        title="Batch payment lands with the payments vertical."
        className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-medium text-white opacity-50"
      >
        <IndianRupee size={14} />
        Pay batch
      </button>
      <button
        type="button"
        disabled
        title="CSV export lands in V1.x."
        className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-medium text-white opacity-50"
      >
        <Download size={14} />
        Export
      </button>
      <button
        type="button"
        onClick={onClear}
        className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        Cancel
      </button>
    </div>
  );
}
